import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet } from 'react-router-dom';
import i18next from 'i18next';
import useU2000AxiosAuthenticated from '../u2000Core/axios/U2000UseAxiosAuthenticated';
import { useU2000InitLUHandlers } from '../u2000Core/hooks/handler/U2000UseInitLUHandlers';
import { U2000HandleApiError, type U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import { useToast } from '../u2000Core/context/toast/U2000ToastContext';
import { useU2000LogicalUnitContext } from '../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import { doInitLU, luHandleAppiError, type U3207InitLU } from './U3207Service';
import { U3207ContextProvider } from './U3207Context';

export const NS = 'u3207';

export const U3207ROLES = {
  READ: 'U3207:READ',
} as const;
export type U3207ROLES = (typeof U3207ROLES)[keyof typeof U3207ROLES];

const defaultInitLU: U3207InitLU = {
  launchSearch: false,
  hideSearch: true,
  nbRecordPerPage: 50,
  defaultMonth: '1970-01',
  tileFamilies: ['provider', 'group', 'conversations', 'user'],
  providers: [],
  groups: [],
};

const U3207: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState(false);
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U3207InitLU>(defaultInitLU);

  useEffect(() => {
    i18next.loadNamespaces(['u3207'], (err) => {
      if (err) return console.error('Error loading namespace:', err);
      setI18nReady(true);
    });
  }, [i18n]);

  const handleInitLU = useU2000InitLUHandlers<U3207InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U3207InitLU) => {
      setInitLUData({
        ...defaultInitLU,
        ...initLU,
        tileFamilies: initLU.tileFamilies?.length
          ? initLU.tileFamilies
          : defaultInitLU.tileFamilies,
        providers: initLU.providers ?? [],
        groups: initLU.groups ?? [],
      });
      setInitLUReady(true);
    },
    onError: ({ error }: { error: unknown }) => {
      const errorResult = U2000HandleApiError(error as Error, t, NS, luHandleAppiError) as U2000ApiErrorResult;
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps

  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;
    setLogicalUnitTitle(t('screenTitle', { ns: NS }));

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, setLogicalUnitTitle, setLogicalUnitBreadcrumb, t]);

  if (!i18nReady || !initLUReady) {
    return null;
  }

  return (
    <U3207ContextProvider initLU={initLUData}>
      <Outlet />
    </U3207ContextProvider>
  );
};

export default U3207;
